import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Autocomplete, Tooltip, FormControlLabel, Switch,
  Stack, LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SaveIcon from '@mui/icons-material/Save';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { booksAPI } from '../../services/api';

const SimilarBooksPanel = ({ bookId, setSnackbar }) => {
  const [loading, setLoading] = useState(false);
  const [similarBooks, setSimilarBooks] = useState([]);
  const [availableBooks, setAvailableBooks] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [sortOrder, setSortOrder] = useState(0);
  const [batchMode, setBatchMode] = useState(false);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [pendingChanges, setPendingChanges] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [findingSimilar, setFindingSimilar] = useState(false);

  const fetchSimilarBooks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await booksAPI.getSimilarBooks(bookId);
      setSimilarBooks(response.data.similarBooks || []);
      
      // Fetch all books to have available options for adding
      const allBooksResponse = await booksAPI.getAllBooks(1, 1000);
      // Filter out the current book and any already similar books
      const existingSimilarIds = response.data.similarBooks?.map(book => book.id) || [];
      const filteredAvailableBooks = allBooksResponse.data.data.filter(
        book => book.id !== bookId && !existingSimilarIds.includes(book.id)
      );
      setAvailableBooks(filteredAvailableBooks);
    } catch (error) {
      console.error('Error fetching similar books:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load similar books',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [bookId, setSnackbar]);

  useEffect(() => {
    fetchSimilarBooks();
  }, [bookId, fetchSimilarBooks]);

  const handleAddBook = async () => {
    if (!selectedBook) return;
    
    try {
      setLoading(true);
      await booksAPI.addSimilarBook(bookId, {
        similarBookId: selectedBook.id,
        sortOrder: sortOrder
      });
      
      setSelectedBook(null);
      setSortOrder(0);
      setDialogOpen(false);
      
      await fetchSimilarBooks();
      
      setSnackbar({
        open: true,
        message: 'Similar book added successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error adding similar book:', error);
      setSnackbar({
        open: true,
        message: 'Failed to add similar book',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveBook = async (similarBookId) => {
    if (batchMode) {
      // In batch mode, just update the state
      setSimilarBooks(prev => prev.filter(book => book.id !== similarBookId));
      setPendingChanges(true);
      return;
    }
    
    // Original functionality for immediate API calls
    if (!window.confirm('Are you sure you want to remove this similar book?')) return;
    
    try {
      setLoading(true);
      await booksAPI.removeSimilarBook(bookId, similarBookId);
      
      await fetchSimilarBooks();
      
      setSnackbar({
        open: true,
        message: 'Similar book removed successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error removing similar book:', error);
      setSnackbar({
        open: true,
        message: 'Failed to remove similar book',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMoveBook = (similarBookId, direction) => {
    // Find the current book and its index
    const currentIndex = similarBooks.findIndex(book => book.id === similarBookId);
    if (currentIndex === -1) return;
    
    // Calculate the new index
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    // Check if the new index is valid
    if (newIndex < 0 || newIndex >= similarBooks.length) return;
    
    // Create a copy of the current order
    const updatedBooks = [...similarBooks];
    
    // Swap the books
    [updatedBooks[currentIndex], updatedBooks[newIndex]] = 
    [updatedBooks[newIndex], updatedBooks[currentIndex]];
    
    // Update the state to reflect changes
    setSimilarBooks(updatedBooks);
    setPendingChanges(true);
  };

  const handleSaveAllChanges = async () => {
    try {
      setLoading(true);
      
      // Extract just the IDs and sort orders
      const similarBookIds = similarBooks.map(book => book.id);
      const sortOrders = similarBooks.map((_, index) => index + 1);
      
      // Call the API with the complete list
      await booksAPI.updateSimilarBooks(bookId, {
        similarBookIds,
        sortOrder: sortOrders
      });
      
      await fetchSimilarBooks();
      setPendingChanges(false);
      
      setSnackbar({
        open: true,
        message: 'Similar books updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating similar books:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update similar books',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBulkAddBooks = async () => {
    if (selectedBooks.length === 0) return;
    
    try {
      setLoading(true);
      
      // Create array of current book IDs
      const currentBookIds = similarBooks.map(book => book.id);
      
      // Add the new selected books
      const newBookIds = [...currentBookIds, ...selectedBooks.map(book => book.id)];
      
      // Create sort orders (1-based indexing)
      const sortOrders = newBookIds.map((_, index) => index + 1);
      
      // Call the API with the complete updated list
      await booksAPI.updateSimilarBooks(bookId, {
        similarBookIds: newBookIds,
        sortOrder: sortOrders
      });
      
      // Reset selected books and close dialog
      setSelectedBooks([]);
      setBulkDialogOpen(false);
      
      // Refresh data
      await fetchSimilarBooks();
      
      setSnackbar({
        open: true,
        message: `${selectedBooks.length} similar books added successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error adding similar books:', error);
      setSnackbar({
        open: true,
        message: 'Failed to add similar books',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFindSimilarBooks = async () => {
    try {
      setFindingSimilar(true);
      
      // Call the API to find similar books
      const response = await booksAPI.findSimilarBooks(bookId);
      
      // Refresh the similar books list
      await fetchSimilarBooks();
      
      // Show success message with the count of books added
      const addedCount = response.data?.addedBooks?.length || 0;
      setSnackbar({
        open: true,
        message: addedCount > 0 
          ? `${addedCount} similar books found and added successfully` 
          : 'No new similar books found',
        severity: addedCount > 0 ? 'success' : 'info'
      });
      
    } catch (error) {
      console.error('Error finding similar books:', error);
      setSnackbar({
        open: true,
        message: 'Failed to find similar books',
        severity: 'error'
      });
    } finally {
      setFindingSimilar(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Similar Books</Typography>
        <Stack direction="row" spacing={2}>
          <FormControlLabel
            control={
              <Switch
                checked={batchMode}
                onChange={() => setBatchMode(!batchMode)}
                color="primary"
              />
            }
            label="Batch Edit Mode"
          />
          
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleFindSimilarBooks}
            disabled={findingSimilar}
          >
            {findingSimilar ? 'Finding...' : 'Find Similar Books'}
          </Button>
          
          {batchMode ? (
            <>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setBulkDialogOpen(true)}
              >
                Add Multiple Books
              </Button>
              
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveAllChanges}
                disabled={!pendingChanges || loading || findingSimilar}
              >
                Save Changes
              </Button>
            </>
          ) : (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Add Similar Book
            </Button>
          )}
        </Stack>
      </Box>

      {pendingChanges && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved changes. Click "Save Changes" to apply them.
        </Alert>
      )}

      {findingSimilar && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            AI is analyzing this book and finding similar titles. This may take a moment...
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {loading && !pendingChanges && !findingSimilar && (
        <Box display="flex" justifyContent="center" my={3}>
          <CircularProgress />
        </Box>
      )}

      {!loading && similarBooks.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No similar books have been added for this title.
        </Alert>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {batchMode && <TableCell width={50}></TableCell>}
                <TableCell>Order</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Author</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {similarBooks.map((book, index) => (
                <TableRow key={book.id}>
                  {batchMode && (
                    <TableCell>
                      <DragIndicatorIcon color="action" />
                    </TableCell>
                  )}
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{book.title}</TableCell>
                  <TableCell>{book.author}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Move Up">
                      <span>
                        <IconButton 
                          size="small"
                          onClick={() => handleMoveBook(book.id, 'up')}
                          disabled={index === 0}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move Down">
                      <span>
                        <IconButton 
                          size="small"
                          onClick={() => handleMoveBook(book.id, 'down')}
                          disabled={index === similarBooks.length - 1}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Remove">
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => handleRemoveBook(book.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Single Add Book Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Similar Book</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={availableBooks}
            getOptionLabel={(option) => `${option.title} by ${option.author}`}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option.title} by <i>{option.author}</i>
              </Box>
            )}
            value={selectedBook}
            onChange={(_, newValue) => setSelectedBook(newValue)}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Select Book" 
                fullWidth 
                margin="normal"
                variant="outlined"
                helperText="Select a book to add as similar"
              />
            )}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Sort Order"
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
            fullWidth
            margin="normal"
            variant="outlined"
            helperText="Lower numbers will appear first (0 will add to the end)"
            inputProps={{ min: 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddBook} 
            variant="contained" 
            color="primary"
            disabled={!selectedBook}
          >
            Add Book
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Bulk Add Books Dialog */}
      <Dialog open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Multiple Similar Books</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            options={availableBooks}
            getOptionLabel={(option) => `${option.title} by ${option.author}`}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                {option.title} by <i>{option.author}</i>
              </Box>
            )}
            value={selectedBooks}
            onChange={(_, newValue) => setSelectedBooks(newValue)}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Select Books" 
                fullWidth 
                margin="normal"
                variant="outlined"
                helperText="Select multiple books to add as similar"
              />
            )}
            sx={{ mb: 2 }}
          />
          
          <Typography variant="body2" color="text.secondary">
            Selected books will be added at the end of the current list.
            You can reorder them after adding.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleBulkAddBooks} 
            variant="contained" 
            color="primary"
            disabled={selectedBooks.length === 0 || loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Add Books
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SimilarBooksPanel;
